import React, {CSSProperties, useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {
  GIFT_SHOP_TRANSACTION_BUILT_IN_KEY_WORD,
  GIFTSHOP_TRANSACTION_DETAIL_DESKTOP_WIDTH,
  GIFTSHOP_TRANSACTION_DETAIL_TABLET_WIDTH,
  WINDOW_MODE_MOBILE_WIDTH,
  WINDOW_MODE_TABLET_WIDTH,
} from 'consts'
import {useTranslation} from 'i18n'
import {useWindowMode} from 'windows'
import {
  getCurrencyValue,
  parseDate,
  showSnackbar,
  useHistory,
  useTransactionDetailCancel,
  useTransactionDetailRateContentHooks,
  useTransactionHowToPay,
} from 'utils'
import {
  GiftShopContentData,
  GiftShopInvoiceDetail,
  TransactionStatus,
} from 'types'
import {
  Button,
  ButtonOutline,
  ConfirmationModal,
  Icon,
  IconFirstBuy,
  Paragraph,
} from 'common/components'
import convertUnit from 'lib/unit'
import {
  GiftShopMessageCreatorRatingModal,
  GiftShopTemplateDetailContentMultipleListModal,
} from 'pages'
import {TransactionDetailContentBuyerProps} from './TransactionDetailContentProps'
import {TransactionDetailWaitingHiresInfo} from '../WaitingHiresInfo'
import {
  TransactionDetailContentItemListMultiCreator,
  TransactionDetailContentItemMultiActions,
  TransactionDetailContentItemListSingleCreator,
} from '../ContentItem'
import {TransactionDetailContentHeader} from '../ContentHeader'

const StyledContainer = styled.div<CSSProperties>`
  display: flex;
  width: ${convertUnit(GIFTSHOP_TRANSACTION_DETAIL_DESKTOP_WIDTH)};
  margin: ${convertUnit(25)};
  padding: ${convertUnit(25)} 0;
  border-radius: ${convertUnit(8)};
  background-color: ${({theme}) => theme.white_1};
  align-self: center;
  overflow: auto;

  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    width: ${convertUnit(GIFTSHOP_TRANSACTION_DETAIL_TABLET_WIDTH)};
    flex-direction: column;
    padding: ${convertUnit(25)};
  }
  @media (max-width: ${WINDOW_MODE_MOBILE_WIDTH}px) {
    width: calc(100% - ${convertUnit(50)});
    min-height: 100%;
    margin: 0;
  }
`

const StyledContentSection = styled.div<CSSProperties>`
  display: flex;
  width: ${({width}) => width};
  flex-direction: column;
  margin: 0 ${convertUnit(25)};
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    width: 100%;
    margin: 0;
    margin-bottom: ${convertUnit(16)};
  }
`

const StyledInfoContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-right: ${convertUnit(25)};
`

const StyledInfoItem = styled.div`
  width: 50%;
  margin-bottom: ${convertUnit(16)};
`

const StyledButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(20)};
`

const StyledButton = styled(Button)`
  display: flex;
  flex: 1;
  width: 100%;
`

const StyledButtonOutline = styled(ButtonOutline)`
  display: flex;
  flex: 1;
  width: 100%;
`

const StyledStrikedText = styled(Paragraph)`
  text-decoration: line-through;
`

const StyledInvoiceIdContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(4)};
`

const StyledCopyIcon = styled(Icon)`
  width: ${convertUnit(8)};
  height: ${convertUnit(8)};
  margin-right: ${convertUnit(8)};
  cursor: pointer;
`

export default function TransactionDetailContentBuyer({
  stateData,
  status: statusState,
  transactionType: transaction_type,
}: TransactionDetailContentBuyerProps) {
  const {translate} = useTranslation()
  const mode = useWindowMode()
  const history = useHistory()

  const {
    invoice_transactions,
    payment_invoice,
    payment_type,
    price_detail,
    status: stateDataStatus,
    checkout_at,
    payment_at,
    payment_method,
    transaction_total,
    card_last4,
  } = stateData
  const isMultiCreator = useMemo(() => invoice_transactions.length > 1, [
    invoice_transactions,
  ])
  const hasPackage = useMemo(
    () =>
      invoice_transactions.filter((item) => item.package.length > 0).length > 0,
    [invoice_transactions],
  )
  const {discount, refund} = price_detail
  const statuses = useMemo(() => {
    const record = invoice_transactions
      .map(({invoice_status}) => invoice_status as TransactionStatus)
      .reduce(
        (prev, curr) => ({
          ...prev,
          [curr]: (prev[curr] ?? 0) + 1,
        }),
        {},
      ) as Record<TransactionStatus, number>

    return [
      {
        status: 'FAILED',
        count: record.FAILED,
      },
      {
        status: 'WAITING_HIRES',
        count: record.WAITING_HIRES,
      },
      {
        status: 'COMPLETED',
        count: record.COMPLETED,
      },
    ].filter(({count}) => count)
  }, [invoice_transactions])
  const singleInvoiceId = invoice_transactions[0].invoice_id

  const {
    handlePostRating,
    handleRateCreator,
    stateCreatorToRate,
    stateSubmitLoading,
    isRatedLocal,
  } = useTransactionDetailRateContentHooks()
  const [creatorToRate, setCreatorToRate] = stateCreatorToRate
  const rateSubmitLoading = stateSubmitLoading[0]

  const {
    stateCancelModal: [cancelModal, setCancelModal],
    handleCancelTransaction,
  } = useTransactionDetailCancel(payment_invoice)

  const {enableHowToPayButton, handleHowToPay} = useTransactionHowToPay({
    payment_invoice,
    payment_type,
    transaction_method: payment_method,
    transaction_date: checkout_at,
    transaction_total,
  })

  const [
    highlightedInvoice,
    setHighlightedInvoice,
  ] = useState<GiftShopInvoiceDetail>()

  const handleClickCollectionItem = useCallback(
    (
      invoice: GiftShopInvoiceDetail,
      item: GiftShopContentData,
      data: GiftShopContentData[],
    ) => {
      if (!data) return

      history.push('giftshop_collection_detail', {
        data: data.map((val) => ({
          ...val,
          upload_source: 'sell',
          creator_id: invoice.creator_id,
          creator_name: invoice.creator_name,
        })),
        showLowResBadge: true,
        selectedItemId: item.content_id,
        limitContent: true,
      })
    },
    [history],
  )

  const handleChatCreator = useCallback(
    (item: GiftShopInvoiceDetail) => {
      const {creator_member_id, content = []} = item
      if (!creator_member_id) return

      history.push('giftshop_message_room', {
        participantId: creator_member_id,
        roomType: 'giftshop',
        chatType: 'request_paid_content',
        isContentClickable: true,
        content:
          content.length > 0
            ? {
                contentIds: content.map((val) => val.content_id),
                invoiceId: item.invoice_id,
                title: content[0].title,
                url: content[0].url,
              }
            : undefined,
      })
    },
    [history],
  )

  const handleRenderContentModal = useMemo(
    () =>
      highlightedInvoice !== undefined && (
        <GiftShopTemplateDetailContentMultipleListModal
          data={{
            content: highlightedInvoice.content,
            package: highlightedInvoice.package,
            creator_name: highlightedInvoice.creator_name,
            price_detail: highlightedInvoice.price_detail,
          }}
          visible={highlightedInvoice !== undefined}
          toggleModal={() => {
            setHighlightedInvoice(undefined)
          }}
          onClickItem={(item) =>
            handleClickCollectionItem(
              highlightedInvoice,
              item,
              highlightedInvoice.content,
            )
          }
          showDiscount
        />
      ),
    [handleClickCollectionItem, highlightedInvoice],
  )

  const handleRenderReviewModal = useMemo(
    () =>
      creatorToRate !== undefined && (
        <GiftShopMessageCreatorRatingModal
          isVerified={creatorToRate.status === 'verified'}
          photo={creatorToRate.photo}
          username={creatorToRate.username}
          visible={creatorToRate !== undefined}
          loadingSubmit={rateSubmitLoading}
          onSubmit={(rating) =>
            handlePostRating(
              creatorToRate.creator_id,
              creatorToRate.invoice_id,
              rating,
            )
          }
          toggleModal={() => setCreatorToRate(undefined)}
        />
      ),
    [creatorToRate, handlePostRating, rateSubmitLoading, setCreatorToRate],
  )

  const handlePaymentMethod = useMemo(() => {
    if (payment_method !== GIFT_SHOP_TRANSACTION_BUILT_IN_KEY_WORD) {
      if (payment_method === 'Credit Card' && transaction_type === 'Purchase') {
        return `${payment_method} ${translate(
          'transaction:creditCardLastFour',
          {number: card_last4},
        )}`
      }
      return payment_method || 'OVO'
    }
    return translate('global:free')
  }, [card_last4, payment_method, transaction_type, translate])

  const handleRenderDetailPurchase = useMemo(
    () => (
      <>
        {transaction_type === 'Purchase' && (
          <StyledInfoItem>
            <Paragraph fontSize="m" fontWeight="medium">
              {translate('giftShop:totalPayment')}
            </Paragraph>
            <Paragraph fontSize="m" fontWeight="bold" color="primary_5">
              {getCurrencyValue(transaction_total)}
            </Paragraph>
            {price_detail.discount.nominal > 0 && (
              <StyledStrikedText fontSize="s" color="gray_3">
                {getCurrencyValue(
                  transaction_total + price_detail.discount.nominal,
                )}
              </StyledStrikedText>
            )}
          </StyledInfoItem>
        )}

        {discount && discount.nominal > 0 && (
          <>
            <StyledInfoItem>
              <Paragraph fontWeight="medium" fontSize="m">
                {translate('giftShop:discount')}
              </Paragraph>
              <Paragraph fontSize="m" fontWeight="bold" color="primary_5">
                {getCurrencyValue(discount.nominal)}
              </Paragraph>
              {!isMultiCreator && (
                <Paragraph fontSize="s" color="primary_5">
                  {translate('giftShop:discountPercent', {
                    count: discount.percentage,
                  })}
                </Paragraph>
              )}
            </StyledInfoItem>
          </>
        )}

        {stateDataStatus === 'WAITING_HIRES' && (
          <TransactionDetailWaitingHiresInfo hasPackage={hasPackage} />
        )}

        {stateDataStatus === 'HALF_COMPLETED' && (
          <>
            <StyledInfoItem>
              <Paragraph fontWeight="medium" fontSize="m">
                {translate('giftShop:totalRefund')}
              </Paragraph>
            </StyledInfoItem>
            <StyledInfoItem>
              <Paragraph color="primary_5" fontSize="m" fontWeight="bold">
                {transaction_total &&
                  translate('giftShop:totalRefundAmount', {
                    price: getCurrencyValue(refund.nominal),
                    percent: refund.percentage,
                  })}
              </Paragraph>
            </StyledInfoItem>
          </>
        )}
      </>
    ),
    [
      transaction_type,
      translate,
      transaction_total,
      price_detail.discount.nominal,
      discount,
      isMultiCreator,
      stateDataStatus,
      hasPackage,
      refund.nominal,
      refund.percentage,
    ],
  )

  const copyInvoiceToClipboard = useCallback(() => {
    navigator.clipboard.writeText(singleInvoiceId)
    showSnackbar(translate('global:invoiceCopied'))
  }, [singleInvoiceId, translate])

  const renderContent = useMemo(
    () => (
      <>
        <StyledContentSection width={'50%'}>
          <TransactionDetailContentHeader
            title={translate('giftShop:transactionType', {
              context: transaction_type.toUpperCase(),
            })}
            subTitle={
              isMultiCreator
                ? `${invoice_transactions.length} ${translate(
                    'global:creators',
                  )}`
                : invoice_transactions[0].creator_name
            }
            statuses={statuses as any}
            subtitleRightElement={
              invoice_transactions[0].is_free_first_buy && (
                <IconFirstBuy text={translate('giftShop:firstPromo')} />
              )
            }
          />
          <StyledInfoContainer>
            {checkout_at && (
              <>
                <StyledInfoItem>
                  <Paragraph fontSize="m" fontWeight="medium">
                    {translate('giftShop:transactionTime')}
                  </Paragraph>
                  <Paragraph fontSize="m">
                    {(checkout_at &&
                      parseDate(checkout_at, 'MMMM D, YYYY, HH:mm')) ||
                      ''}
                  </Paragraph>
                </StyledInfoItem>
              </>
            )}
            {payment_at && (
              <>
                <StyledInfoItem>
                  <Paragraph fontSize="m" fontWeight="medium">
                    {translate('giftShop:transactionPaymentTime')}
                  </Paragraph>
                  <Paragraph fontSize="m">
                    {(payment_at &&
                      parseDate(payment_at, 'MMMM D, YYYY, HH:mm')) ||
                      ''}
                  </Paragraph>
                </StyledInfoItem>
              </>
            )}
            <StyledInfoItem>
              <Paragraph fontSize="m" fontWeight="medium">
                {translate('giftShop:paymentMethod')}
              </Paragraph>
              <Paragraph fontSize="m">{handlePaymentMethod}</Paragraph>
            </StyledInfoItem>
            {invoice_transactions.length === 1 && (
              <StyledInfoItem>
                <StyledInvoiceIdContainer>
                  <Paragraph fontSize="m" fontWeight="medium">
                    {translate('giftShop:invoiceID')}
                  </Paragraph>
                  <StyledCopyIcon
                    type="copy"
                    onClick={copyInvoiceToClipboard}
                    color="primary_5"
                    size={12}
                  />
                </StyledInvoiceIdContainer>
                <Paragraph
                  fontSize="m"
                  fontWeight="bold"
                  color="primary_5"
                  onClick={copyInvoiceToClipboard}>
                  {singleInvoiceId}
                </Paragraph>
              </StyledInfoItem>
            )}
            {transaction_type === 'Purchase' && handleRenderDetailPurchase}
            {transaction_type === 'Refund' && (
              <StyledInfoItem>
                <Paragraph fontSize="m" fontWeight="medium">
                  {translate('giftShop:totalRefund')}
                </Paragraph>
                <Paragraph fontSize="m" fontWeight="bold" color="primary_5">
                  {getCurrencyValue(price_detail?.refund_collector.nominal)}
                </Paragraph>
              </StyledInfoItem>
            )}
          </StyledInfoContainer>

          <StyledButtonsContainer>
            {transaction_type === 'Purchase' && statusState === 'PROCESSED' && (
              <>
                <StyledButtonOutline
                  borderColor="gray_5"
                  color="gray_5"
                  label={translate('global:cancel')}
                  onClick={() => setCancelModal(true)}
                />
                {enableHowToPayButton && (
                  <StyledButton
                    label={translate('giftShop:howToPay')}
                    onClick={handleHowToPay}
                  />
                )}
              </>
            )}
          </StyledButtonsContainer>
        </StyledContentSection>
        {isMultiCreator ? (
          <TransactionDetailContentItemListMultiCreator
            mode={mode}
            data={invoice_transactions}
            onClickItem={(item) => {
              setHighlightedInvoice(item)
            }}
            handleRenderEntryBottom={(item) =>
              !item.is_self_buy && (
                <TransactionDetailContentItemMultiActions
                  invoice={item}
                  isRated={isRatedLocal}
                  isRateable={
                    item.invoice_status !== 'WAITING_HIRES' &&
                    transaction_type === 'Purchase' &&
                    !item.content.some((dt) => dt.quality !== 'hi-res')
                  }
                  onChatCreator={handleChatCreator}
                  onRateCreator={handleRateCreator}
                />
              )
            }
            showDiscount
          />
        ) : (
          <TransactionDetailContentItemListSingleCreator
            mode={mode}
            contents={invoice_transactions[0].content}
            packages={invoice_transactions[0].package}
            onClickItem={(item) => {
              handleClickCollectionItem(
                invoice_transactions[0],
                item,
                invoice_transactions[0].content,
              )
            }}
            discount={discount}
            showDiscount
            style={{
              marginBottom: mode === 'mobile' ? convertUnit(50) : 0,
            }}
            underTitleElement={
              !invoice_transactions[0].is_self_buy && (
                <TransactionDetailContentItemMultiActions
                  style={{
                    marginTop: convertUnit(4),
                    marginBottom: convertUnit(6),
                  }}
                  invoice={invoice_transactions[0]}
                  isRated={isRatedLocal}
                  isRateable={
                    invoice_transactions[0].invoice_status !==
                      'WAITING_HIRES' &&
                    transaction_type === 'Purchase' &&
                    !invoice_transactions[0].content.some(
                      (dt) => dt.quality !== 'hi-res',
                    )
                  }
                  onChatCreator={handleChatCreator}
                  onRateCreator={handleRateCreator}
                />
              )
            }
          />
        )}
      </>
    ),
    [
      translate,
      transaction_type,
      isMultiCreator,
      invoice_transactions,
      statuses,
      checkout_at,
      payment_at,
      handlePaymentMethod,
      handleRenderDetailPurchase,
      price_detail?.refund_collector.nominal,
      copyInvoiceToClipboard,
      singleInvoiceId,
      statusState,
      enableHowToPayButton,
      handleHowToPay,
      mode,
      discount,
      isRatedLocal,
      handleChatCreator,
      handleRateCreator,
      setCancelModal,
      handleClickCollectionItem,
    ],
  )

  const handleRenderCancelTransactionModal = useMemo(
    () => (
      <ConfirmationModal
        visible={cancelModal}
        title={translate('global:cancel')}
        text={translate('transaction:cancelTransactionModalMessage')}
        cancelText={translate('global:stay')}
        confirmText={translate('global:yesImSure')}
        onConfirm={handleCancelTransaction}
        toggleModal={() => setCancelModal(false)}
      />
    ),
    [cancelModal, handleCancelTransaction, setCancelModal, translate],
  )

  return (
    <StyledContainer>
      {renderContent}
      {handleRenderReviewModal}
      {handleRenderCancelTransactionModal}
      {handleRenderContentModal}
    </StyledContainer>
  )
}
